import React, { useState, useEffect, useCallback } from "react";
import { HeartPulse, RefreshCw, Clock } from "lucide-react";
import api from "../api/client";
import HealthSummaryCards from "./health/HealthSummaryCards";
import HealthFindingsTable from "./health/HealthFindingsTable";
import ServiceStatusPanel from "./health/ServiceStatusPanel";

const TABS = ["Findings", "Scan History", "API Status"];

export default function SiteHealth({ selectedSite }) {
  const [tab, setTab] = useState(0);
  const [summary, setSummary] = useState(null);
  const [findings, setFindings] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [severityFilter, setSeverityFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("open");

  const siteId = selectedSite?.id;

  const fetchData = useCallback(() => {
    if (!siteId) return;
    setLoading(true);

    const params = {};
    if (severityFilter) params.severity = severityFilter;
    if (statusFilter) params.status = statusFilter;

    Promise.all([
      api.get(`/websites/${siteId}/health/summary/`),
      api.get(`/websites/${siteId}/health/findings/`, { params }),
      api.get(`/websites/${siteId}/health/runs/`),
    ])
      .then(([sumRes, findRes, runRes]) => {
        setSummary(sumRes.data);
        setFindings(findRes.data.results || findRes.data);
        setRuns(runRes.data.results || runRes.data);
      })
      .catch((err) => console.error("Health fetch error", err))
      .finally(() => setLoading(false));
  }, [siteId, severityFilter, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleScan = async (scanType) => {
    if (!siteId || scanning) return;
    setScanning(true);
    try {
      await api.post(`/websites/${siteId}/health/scan/`, {
        scan_type: scanType,
      });
      fetchData();
    } catch (err) {
      console.error("Scan trigger error", err);
    } finally {
      setScanning(false);
    }
  };

  if (!selectedSite) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        Select a website to view health data.
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <HeartPulse className="w-6 h-6 text-copper" />
          <h1 className="text-xl font-bold">Site Health</h1>
        </div>
        <div className="flex items-center gap-2">
          {summary?.last_scan && (
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Last scan:{" "}
              {new Date(summary.last_scan.started_at).toLocaleString()}
            </span>
          )}
          <button
            onClick={() => handleScan("critical")}
            disabled={scanning}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-dark-300 border border-dark-50 text-gray-300 hover:border-copper hover:text-copper disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${scanning ? "animate-spin" : ""}`} />
            Quick Scan
          </button>
          <button
            onClick={() => handleScan("full")}
            disabled={scanning}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-copper/10 border border-copper/30 text-copper hover:bg-copper/20 disabled:opacity-50"
          >
            Full Scan
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <HealthSummaryCards summary={summary} />

      {/* Tabs */}
      <div className="flex gap-1 border-b border-dark-50 mb-4">
        {TABS.map((t, i) => (
          <button
            key={t}
            onClick={() => setTab(i)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === i
                ? "border-copper text-copper"
                : "border-transparent text-gray-500 hover:text-gray-300"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-6 h-6 text-gray-600 animate-spin" />
        </div>
      ) : (
        <>
          {tab === 0 && (
            <HealthFindingsTable
              findings={findings}
              siteId={siteId}
              onUpdate={fetchData}
              severityFilter={severityFilter}
              setSeverityFilter={setSeverityFilter}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
            />
          )}

          {tab === 1 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-500 text-xs border-b border-dark-50">
                    <th className="text-left py-2 px-3">Type</th>
                    <th className="text-left py-2 px-3">Status</th>
                    <th className="text-left py-2 px-3">Pages</th>
                    <th className="text-left py-2 px-3">New</th>
                    <th className="text-left py-2 px-3">Resolved</th>
                    <th className="text-left py-2 px-3">Started</th>
                    <th className="text-left py-2 px-3">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center text-gray-600 py-8">
                        No scan runs yet.
                      </td>
                    </tr>
                  )}
                  {runs.map((r) => {
                    const duration =
                      r.started_at && r.finished_at
                        ? Math.round(
                            (new Date(r.finished_at) - new Date(r.started_at)) /
                              1000
                          )
                        : null;
                    return (
                      <tr key={r.id} className="border-b border-dark-50">
                        <td className="py-2 px-3 capitalize">{r.scan_type}</td>
                        <td className="py-2 px-3">
                          <span
                            className={`text-xs px-2 py-0.5 rounded ${
                              r.status === "completed"
                                ? "bg-green-900/20 text-green-400"
                                : r.status === "running"
                                ? "bg-amber-900/20 text-amber-400"
                                : "bg-red-900/20 text-red-400"
                            }`}
                          >
                            {r.status}
                          </span>
                        </td>
                        <td className="py-2 px-3 font-mono">{r.pages_scanned}</td>
                        <td className="py-2 px-3 font-mono">{r.findings_created}</td>
                        <td className="py-2 px-3 font-mono">{r.findings_resolved}</td>
                        <td className="py-2 px-3 text-xs text-gray-500">
                          {new Date(r.started_at).toLocaleString()}
                        </td>
                        <td className="py-2 px-3 text-xs text-gray-500">
                          {duration != null ? `${duration}s` : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {tab === 2 && (
            <ServiceStatusPanel services={summary?.services || []} />
          )}
        </>
      )}
    </div>
  );
}
